// FILE: components/Table.tsx
import React from 'react';
import { RawCheckbox } from './Checkbox';

// --- TABLE COMPONENT ---
interface TableContextProps {
    striped: boolean;
    hoverable: boolean;
}
const TableContext = React.createContext<TableContextProps>({ striped: false, hoverable: false });
const useTableContext = () => React.useContext(TableContext);

type TableRootProps = {
    children: React.ReactNode;
    className?: string;
    striped?: boolean;
    hoverable?: boolean;
    compact?: boolean;
    bordered?: boolean; // Global border control
    headerBordered?: boolean; // Header border control
    bodyBordered?: boolean; // Body border control
    footerBordered?: boolean; // Footer border control
};

const TableRoot: React.FC<TableRootProps> = ({ 
    children, 
    className = '', 
    striped = false, 
    hoverable = false, 
    compact = false,
    bordered = true,
    headerBordered = true,
    bodyBordered = true,
    footerBordered = true
}) => (
    <div className={`relative overflow-x-auto rounded-lg border border-border-color dark:border-dark-border-color w-full custom-scrollbar ${className}`}>
        <TableContext.Provider value={{ striped, hoverable }}>
            <table className={`w-full text-left text-text-secondary dark:text-dark-text-secondary ${
                compact ? 'text-xs' : 'text-sm'
            }`}>
                {children}
            </table>
        </TableContext.Provider>
    </div>
);
TableRoot.displayName = 'Table.Root';

const TableHeader: React.FC<{ 
    children: React.ReactNode; 
    className?: string;
    bordered?: boolean; // Override global header border
} & React.ThHTMLAttributes<HTMLTableSectionElement>> = ({ 
    children, 
    className = '', 
    bordered: headerBordered,
    ...props 
}) => (
    <thead className={`text-xs text-text-primary dark:text-dark-text-primary uppercase bg-bg-tertiary dark:bg-dark-bg-tertiary ${className}`} {...props}>
        {children}
    </thead>
);
TableHeader.displayName = 'Table.Header';

const TableBody: React.FC<{ 
    children: React.ReactNode; 
    className?: string;
    bordered?: boolean; // Override global body border
} & React.HTMLAttributes<HTMLTableSectionElement>> = ({ 
    children, 
    className = '', 
    bordered: bodyBordered,
    ...props 
}) => (
    <tbody className={`bg-bg-secondary dark:bg-dark-bg-secondary divide-y divide-border-color dark:divide-dark-border-color ${className}`} {...props}>
        {children}
    </tbody>
);
TableBody.displayName = 'Table.Body';

const TableFooter: React.FC<{ 
    children: React.ReactNode; 
    className?: string;
    bordered?: boolean; // Override global footer border
} & React.HTMLAttributes<HTMLTableSectionElement>> = ({ 
    children, 
    className = '', 
    bordered: footerBordered,
    ...props 
}) => (
    <tfoot className={`bg-bg-tertiary dark:bg-dark-bg-tertiary text-xs text-text-secondary dark:text-dark-text-secondary ${className}`} {...props}>
        {children}
    </tfoot>
);
TableFooter.displayName = 'Table.Footer';

const TableRow: React.FC<{ 
    children: React.ReactNode; 
    className?: string; 
    selected?: boolean;
    highlighted?: boolean;
    highlightColor?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}> = ({ 
    children, 
    className = '', 
    selected = false, 
    highlighted = false,
    highlightColor = 'primary'
}) => {
    const { striped, hoverable } = useTableContext();
    const hoverClasses = hoverable ? 'hover:bg-bg-tertiary/80 dark:hover:bg-dark-bg-tertiary/60' : '';
    const stripedClasses = striped ? 'odd:bg-bg-tertiary/50 dark:odd:bg-dark-bg-tertiary/30' : '';
    const selectedClasses = selected ? '!bg-brand-orange/10 dark:!bg-dark-brand-orange/10' : '';
    
    // Highlight classes
    const highlightClasses = highlighted ? {
        primary: '!bg-brand-orange/10 dark:!bg-dark-brand-orange/10',
        secondary: '!bg-brand-navy/10 dark:!bg-dark-brand-navy/10',
        success: '!bg-system-success/10 dark:!bg-system-success/20',
        warning: '!bg-system-warning/10 dark:!bg-system-warning/20',
        error: '!bg-system-error/10 dark:!bg-system-error/20',
        info: '!bg-system-info/10 dark:!bg-system-info/20'
    }[highlightColor] : '';
    
    const combinedClasses = `transition-colors ${hoverClasses} ${stripedClasses} ${selectedClasses} ${highlightClasses} ${className}`;
    return <tr className={combinedClasses}>{children}</tr>;
};
TableRow.displayName = 'Table.Row';

const TableHeadCell: React.FC<{ children: React.ReactNode; className?: string } & React.ThHTMLAttributes<HTMLTableHeaderCellElement>> = ({ children, className = '', scope = 'col', ...props }) => (
    <th scope={scope} className={`px-6 py-4 font-bold ${className}`} {...props}>
        {children}
    </th>
);
TableHeadCell.displayName = 'Table.HeadCell';

const TableCell: React.FC<{ 
    children: React.ReactNode; 
    className?: string;
    highlighted?: boolean;
    highlightColor?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
} & React.TdHTMLAttributes<HTMLTableCellElement>> = ({ 
    children, 
    className = '', 
    highlighted = false,
    highlightColor = 'primary',
    ...props 
}) => {
    // Highlight classes for cells
    const highlightClasses = highlighted ? {
        primary: '!bg-brand-orange/10 dark:!bg-dark-brand-orange/10',
        secondary: '!bg-brand-navy/10 dark:!bg-dark-brand-navy/10',
        success: '!bg-system-success/10 dark:!bg-system-success/20',
        warning: '!bg-system-warning/10 dark:!bg-system-warning/20',
        error: '!bg-system-error/10 dark:!bg-system-error/20',
        info: '!bg-system-info/10 dark:!bg-system-info/20'
    }[highlightColor] : '';
    
    return (
        <td className={`px-6 py-4 ${highlightClasses} ${className}`} {...props}>
            {children}
        </td>
    );
};
TableCell.displayName = 'Table.Cell';

type TableCheckboxProps = {
    id: string;
    label: string; // for accessibility
    isHeader?: boolean;
} & Omit<React.ComponentProps<typeof RawCheckbox>, 'ref'>;

const TableCheckbox: React.FC<TableCheckboxProps> = ({ id, label, isHeader = false, ...props }) => {
    const CellComponent = isHeader ? 'th' : 'td';
    return (
        <CellComponent scope={isHeader ? 'col' : undefined} className="px-4 py-4 w-4">
             <label htmlFor={id} className="sr-only">{label}</label>
             <RawCheckbox id={id} {...props} />
        </CellComponent>
    );
};
TableCheckbox.displayName = "Table.Checkbox";

export const Table = Object.assign(TableRoot, {
    Header: TableHeader,
    Body: TableBody,
    Footer: TableFooter,
    Row: TableRow,
    HeadCell: TableHeadCell,
    Cell: TableCell,
    Checkbox: TableCheckbox
});